/**
 * fachfreigabeModell — EINE reine Funktion, die entscheidet, ob und wie ein Beitrag
 * als „wartet auf fachliche Freigabe" gekennzeichnet wird.
 *
 * WOFÜR (Fund 21.08.2026)
 * ----------------------
 * Seit dem 19.08. hält ein Tor Beiträge zurück, die eine sicherheitskritische
 * Handlungsanweisung enthalten — nachdem eine KI-geschriebene Erste-Hilfe-Anweisung
 * unter dem Namen eines zertifizierten Ausbilders veröffentlicht worden war. Das Tor
 * wirkt: beim zahlenden Kunden hängen **50 Beiträge** daran, gemessen am 21.08.
 * (`fachfreigabe_noetig` gesetzt, `fachfreigabe` bei keinem einzigen).
 *
 * Nur sah der Kunde davon **nichts**. `grep -ril "fachfreigabe" frontend/src/` fand
 * null Treffer; in seinem Monatsplan sahen die 50 Beiträge aus wie normale Entwürfe.
 * Der Nachlauf schrieb jede Nacht „48 freigegebene Beiträge, aber KEINER eingeplant"
 * ins Serverlog — dorthin, wo kein Kunde je hinsieht.
 *
 * Das Feld lag dabei längst vor: die Plan-Route projiziert mit `{"_id": 0}`, liefert
 * also alles mit. Es fehlte ausschließlich die Anzeige.
 *
 * BEWUSST NICHT HIER
 * ------------------
 * Diese Datei kennzeichnet, sie öffnet nicht. Ein Knopf, der das Tor aufmacht,
 * verschiebt eine Sicherheitsgrenze um medizinische Handlungsanweisungen; wer die
 * Freigabe erteilen darf, ist eine Inhaber-Entscheidung (siehe TODO.md).
 *
 * Kein React, keine Seiteneffekte — die Entscheidung ist die Aussage, das Aussehen
 * des Hinweises ist es nicht (gleiche Linie wie `channelButtonModel.js`).
 */

/** Ein Mensch hat diesen Text fachlich verantwortet.
 *
 * Spiegelt `autopilot/fachfreigabe.ist_fachlich_freigegeben`: gesucht wird ein
 * gesetzter, nicht-leerer Wert — bei der Objektform der Name unter `von`. Bewusst
 * KEINE Deutung des Inhalts (Datum parsen, Namen prüfen): wer anfängt zu deuten,
 * baut eine Lücke, durch die ein Tippfehler das Tor öffnet.
 */
export function istFachlichFreigegeben(post) {
  const wert = post?.fachfreigabe;
  if (wert && typeof wert === "object") {
    return String(wert.von ?? "").trim().length > 0;
  }
  return String(wert ?? "").trim().length > 0;
}

/** Rohen Grund aus der Datenbank in einen lesbaren Satz überführen.
 *
 * Der Erkenner liefert Formen wie „beschreibt ein Verfahren (herzdruckmassage)" oder
 * „Notfallthema (notruf) in Anweisungsform [leg]". Der Kunde soll das Stichwort sehen,
 * nicht die Klammer-Notation des Erkenners.
 */
export function grundText(grund) {
  const roh = String(grund ?? "").trim();
  if (!roh) return "";
  const treffer = roh.match(/\(([^)]+)\)/);
  const stichwort = treffer ? treffer[1].trim() : "";
  if (!stichwort) return roh;
  // Erstes Zeichen groß — der Erkenner arbeitet kleingeschrieben.
  const schoen = stichwort.charAt(0).toUpperCase() + stichwort.slice(1);
  if (/^Notfallthema/i.test(roh)) return `Notfallthema: ${schoen}`;
  return `Beschreibt ein Verfahren: ${schoen}`;
}

/**
 * Entscheidet die Kennzeichnung EINES Beitrags.
 *
 * @returns {{markiert: boolean, grund: string, hinweis: string}}
 *   markiert=false ⇒ nichts anzeigen. Der Aufrufer muss nur `markiert` prüfen.
 */
export function fachfreigabeModell(post) {
  const nichts = { markiert: false, grund: "", hinweis: "" };
  if (!post || typeof post !== "object") return nichts;

  // Bereits verantwortet ⇒ kein Hinweis mehr, auch wenn die alte Markierung am
  // Dokument stehen bleibt. Das Tor entscheidet über `fachfreigabe`, nicht über
  // `fachfreigabe_noetig` — die Anzeige muss derselben Reihenfolge folgen.
  if (istFachlichFreigegeben(post)) return nichts;

  const markierung = post.fachfreigabe_noetig;
  if (!markierung) return nichts;

  const grund = grundText(
    typeof markierung === "object" ? markierung.grund : markierung
  );
  return {
    markiert: true,
    grund,
    hinweis:
      "Dieser Beitrag enthält eine sicherheitskritische Handlungsanweisung und wird " +
      "deshalb nicht automatisch veröffentlicht, bevor ein fachkundiger Mensch ihn " +
      "gelesen und verantwortet hat.",
  };
}

/** Zählt die wartenden Beiträge einer Liste — für die Sammelzeile über dem Plan. */
export function zaehleWartende(posts) {
  if (!Array.isArray(posts)) return 0;
  return posts.reduce((n, p) => n + (fachfreigabeModell(p).markiert ? 1 : 0), 0);
}
